// src/features/assistant-connect/i18n/es.ts
// «Conecte su asistente de IA»: cómo conecta cada persona su propio asistente a Insight.
// Los nombres de menús de cada asistente se citan como los muestra su fabricante.

export default {
  name: 'Asistente de IA',
  entry: 'Asistente de IA',
  entryTooltip: 'Conecte su asistente de IA a Insight',
  title: 'Conecte su asistente de IA',
  subtitle: 'Consulte Insight conversando con el asistente que ya utiliza.',
  accessDenied: 'La conexión de asistentes no está disponible para su perfil.',

  how: {
    title: 'Cómo funciona',
    account:
      'El asistente trabaja con su cuenta de Insight: actúa como usted y ve los mismos establecimientos que usted ve en estas pantallas, ni uno más.',
    readOnly:
      'En esta versión solo consulta: puede consultar, no cambia nada. Los datos de Insight no se modifican desde el asistente.',
    consent:
      'La primera vez se abre la página de inicio de sesión de Roommatik y después una pantalla que dice «Va a conectar <asistente> con su cuenta de Roommatik Insight…»: acéptela para terminar.',
    disconnect:
      'Puede desconectarlo cuando quiera desde los ajustes de conectores de su asistente; en cada pestaña se indica dónde. Desconectado, el asistente deja de usar su cuenta.',
  },

  environment: {
    test: 'Este es Insight de pruebas: la dirección del servidor conecta el asistente con el entorno de pruebas, no con el de producción.',
    unknown: 'No se reconoce la dirección de este Insight; la dirección del servidor que se muestra es la del entorno de pruebas.',
  },

  copy: 'Copiar',
  copied: 'Copiado',

  connection: {
    title: 'Datos de conexión',
    serverUrl: 'Dirección del servidor',
    clientId: 'ID de cliente',
    secret: 'Secreto de cliente: déjelo en blanco.',
  },

  tabsLabel: 'Asistentes',
  requirements: 'Requisitos',
  disconnectLabel: 'Para desconectarlo',

  assistants: {
    claude: {
      name: 'Claude',
      disconnect:
        'En claude.ai, «Settings» › «Connectors»: elija el conector y desconéctelo o elimínelo. En Claude Code, escriba «claude mcp remove insight» en su terminal.',
      sections: {
        apps: {
          title: 'Claude en la web, el escritorio y el móvil',
          requirements:
            'Planes Free (un conector propio), Pro y Max. En Team y Enterprise lo añade un propietario de la organización en su configuración; después cada miembro se conecta.',
          step1: 'En claude.ai, abra «Settings» › «Connectors» y pulse «Add custom connector».',
          step2: 'Escriba un nombre (por ejemplo, Roommatik Insight) y pegue la dirección del servidor.',
          step3: 'Abra «Advanced settings», pegue el ID de cliente, deje el secreto en blanco y pulse «Add».',
          step4:
            'Pulse «Connect» e inicie sesión con su cuenta de Insight. Las aplicaciones de escritorio y de móvil usan el mismo conector, sin configurarlo otra vez.',
          note: 'Los datos de inicio de sesión no se pueden cambiar después de añadir el conector: si se equivoca, elimínelo y añádalo de nuevo.',
        },
        code: {
          title: 'Claude Code',
          requirements: 'Cualquier plan que incluya Claude Code.',
          step1: 'Escriba esta orden en su terminal (ya lleva la dirección y el ID de cliente):',
          step2: 'En Claude Code, escriba /mcp, elija «insight» y pulse «Authenticate»: se abre el navegador para iniciar sesión.',
        },
      },
    },
    chatgpt: {
      name: 'ChatGPT',
      disconnect: '«Settings» › «Apps & Connectors»: elija la aplicación y desconéctela o elimínela.',
      sections: {
        web: {
          title: 'ChatGPT en la web',
          requirements:
            'Solo en la web. Para que pueda hacer cambios necesita ChatGPT Business, Enterprise o Edu, con el modo de desarrollador activado por un administrador. En Pro solo puede consultar. Para Plus, OpenAI no indica disponibilidad.',
          step1:
            'En Business, Enterprise o Edu, pida a un administrador de su espacio de trabajo que active el modo de desarrollador; puede además publicar la aplicación para todo el equipo.',
          step2: 'En chatgpt.com, abra «Settings» › «Apps & Connectors» › «Advanced settings», active «Developer mode» y pulse «Create».',
          step3: 'Escriba un nombre, pegue la dirección del servidor y elija la autenticación «OAuth».',
          step4:
            'En las opciones avanzadas, elija un cliente definido por usted («User-Defined OAuth Client»), pegue el ID de cliente, deje el secreto en blanco y guarde.',
          step5: 'Inicie sesión con su cuenta de Insight cuando se lo pida y elija la aplicación en una conversación nueva.',
        },
      },
    },
    copilot: {
      name: 'GitHub Copilot en VS Code',
      disconnect: 'En la paleta de comandos, «MCP: List Servers»: elija insight y quítelo.',
      sections: {
        vscode: {
          title: 'GitHub Copilot en Visual Studio Code',
          requirements:
            'Copilot Free, Pro y Pro+ lo permiten. En Copilot Business y Enterprise, un administrador de la organización debe activar la directiva «MCP servers in Copilot», que viene desactivada.',
          step1: 'Abra la paleta de comandos (Ctrl+Mayús+P) y ejecute «MCP: Add Server».',
          step2: 'Elija «HTTP», pegue la dirección del servidor y llámelo insight. Si lo prefiere, escríbalo en el archivo mcp.json:',
          step3: 'Cuando le pida un ID de cliente, pegue el de esta pestaña, deje el secreto en blanco e inicie sesión en el navegador.',
          step4: 'Abra el chat de Copilot en modo «Agent» y pregunte.',
        },
      },
    },
    cursor: {
      name: 'Cursor',
      disconnect: '«Cursor Settings» › «MCP»: apague o elimine insight.',
      sections: {
        ide: {
          title: 'Cursor',
          requirements: 'Cursor no indica requisitos de plan. En los equipos, un administrador puede limitar qué servidores se permiten.',
          step1: 'Abra «Cursor Settings» › «MCP» y pulse «Add new MCP server»: se abre el archivo mcp.json.',
          step2: 'Añada este bloque, que ya lleva la dirección y el ID de cliente, y guarde:',
          step3: 'Vuelva a los ajustes, pulse «Connect» junto a insight e inicie sesión en el navegador.',
        },
      },
    },
  },

  tasks: {
    title: 'Qué puede preguntarle',
    subtitle: 'Escríbale con sus propias palabras, como se lo pediría a un compañero. Con su perfil puede pedirle:',
    items: {
      properties: {
        title: 'Ver sus establecimientos',
        summary: 'Qué establecimientos puede consultar y a qué cadena pertenece cada uno.',
      },
      indicators: {
        title: 'Consultar los indicadores de un establecimiento',
        summary: 'Los mismos indicadores de Insight para el periodo que usted indique.',
      },
      comparisons: {
        title: 'Comparar periodos y establecimientos',
        summary: 'Cómo va este mes frente al anterior, o qué establecimiento rinde mejor en un indicador.',
      },
      arrivals: {
        title: 'Ver las llegadas de hoy',
        summary: 'Cuántas llegadas hay, cuántas han hecho el check-in y cuántas faltan.',
      },
      checkIns: {
        title: 'Revisar los check-ins',
        summary: 'Los check-ins de un periodo y el recorrido de una reserva, con los datos personales ocultos.',
      },
      alerts: {
        title: 'Revisar alertas y errores',
        summary: 'Qué alertas hay abiertas y qué significa un código de error.',
      },
      devices: {
        title: 'Conocer el estado de los equipos',
        summary: 'Qué equipos están conectados, cuáles no responden y desde cuándo.',
      },
      manual: {
        title: 'Consultar el manual',
        summary: 'Cómo se hace algo en Insight, con la página que lo explica.',
      },
    },
  },
};
